import type { GoLiveGateVerdict, GoLiveStatusTone } from '../../../apis/types'

// Maps the backend gate verdict onto the same green / red / amber / slate
// palette the reconciliation legend uses, so the app stays visually
// consistent. `dot` is a small solid swatch class for the chip squares.
// Gate order and display labels are backend-driven (gate.label) — the
// frontend only chooses a color per verdict.
interface GateStyle {
    dot: string
    label: string
}

export const gateVerdictStyles: Record<GoLiveGateVerdict, GateStyle> = {
    pass: { dot: 'bg-green-500 dark:bg-green-400', label: 'Pass' },
    'fail-ours': { dot: 'bg-red-500 dark:bg-red-400', label: 'Fail (ours)' },
    'waiting-external': { dot: 'bg-amber-500 dark:bg-amber-400', label: 'Waiting on utility' },
    unknown: { dot: 'bg-slate-400 dark:bg-slate-500', label: "Can't-see-yet" },
}

// Verdicts in legend display order.
export const verdictOrder: GoLiveGateVerdict[] = ['pass', 'fail-ours', 'waiting-external', 'unknown']

// Human display labels for the owner slugs the backend emits (see FleetGateVO.owner).
// The Owner chip showed the raw kebab slug uppercased; this is the first place
// those slugs get a friendly label. `utility` is the only external (non-KE) owner.
export const ownerLabels: Record<string, string> = {
    'tenant-team': 'Tenant Team',
    operations: 'Operations',
    'tech-team': 'Tech Team',
    'billing-ops': 'Billing Ops',
    'salesforce-ops': 'Salesforce Ops',
    'utility-relationships': 'Utility Relations',
    utility: 'Utility',
}

// Friendly label for an owner slug, falling back to the raw slug if unmapped.
export function ownerLabel(owner: string | null): string | null {
    if (owner == null) return null
    return ownerLabels[owner] ?? owner
}

// Status chip tones (program/property "Status" column). Reuses the
// reconciliation rounded-full ring-1 ring-inset chip look.
export const statusToneStyles: Record<GoLiveStatusTone, string> = {
    blocking: 'bg-red-50 dark:bg-red-400/10 text-red-700 dark:text-red-400 ring-red-600/20 dark:ring-red-400/20',
    waiting: 'bg-amber-50 dark:bg-amber-400/10 text-amber-700 dark:text-amber-400 ring-amber-600/20 dark:ring-amber-400/20',
    ready: 'bg-green-50 dark:bg-green-400/10 text-green-700 dark:text-green-400 ring-green-600/20 dark:ring-green-400/20',
    review: 'bg-blue-50 dark:bg-blue-400/10 text-blue-700 dark:text-blue-400 ring-blue-600/20 dark:ring-blue-400/20',
}
